import React from "react";

/**
 * Interface for the UserIcon component props.
 * @interface UserIconProps
 * @property {string} [className] - Optional CSS class name for styling the SVG
 * @property {string} [color] - Optional color for the user icon (default: 'currentColor')
 * @property {string} [size] - Optional size for the icon (default: '0.8rem')
 * @property {React.MouseEventHandler<SVGSVGElement>} [onClick] - Optional click handler function
 * @property {React.CSSProperties} [style] - Optional additional inline styles
 */
interface UserIconProps {
  className?: string;
  color?: string;
  size?: string;
  onClick?: React.MouseEventHandler<SVGSVGElement>;
  style?: React.CSSProperties;
}

/**
 * A customizable user icon component.
 * Used for user profile indicators, login buttons, or other user-related UI elements.
 *
 * @component
 * @example
 * // User icon with default color and size
 * <UserIcon />
 *
 * @example
 * // User icon with custom color, size, class and click handler
 * <UserIcon
 *   color="#FF0000"
 *   size="1.5rem"
 *   className="profile-icon"
 *   onClick={() => handleUserClick()}
 * />
 *
 * @param {UserIconProps} props - Component props
 * @param {string} [props.className=''] - CSS class name for additional styling
 * @param {string} [props.color='currentColor'] - Fill color of the icon
 * @param {string} [props.size='0.8rem'] - Size of the icon
 * @param {React.MouseEventHandler<SVGSVGElement>} [props.onClick] - Click handler function
 * @param {React.CSSProperties} [props.style] - Additional inline styles
 * @returns {JSX.Element} SVG user icon
 */
const UserIcon: React.FC<UserIconProps> = ({
  className = "",
  color = "currentColor",
  size = "0.8rem",
  onClick,
  style,
}) => {
  return (
    <svg
      viewBox="0 0 256 256"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        display: "inline-block",
        verticalAlign: "middle",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      <rect fill="none" height="256" width="256" />
      <circle cx="128" cy="120" r="44" fill={color} />
      <path
        d="M128,24A104,104,0,1,0,232,128,104.2,104.2,0,0,0,128,24Zm65.8,162.4a81.3,81.3,0,0,0-24.5-23,59.7,59.7,0,0,1-82.6,0,81.3,81.3,0,0,0-24.5,23,88,88,0,1,1,131.6,0Z"
        fill={color}
      />
    </svg>
  );
};

export default UserIcon;
